import {
    TooltipContent, TooltipProvider, TooltipTrigger, Tooltip
} from 'shared/components/ui/tooltip';
import {
    Badge
} from 'shared/components/ui/badge';
import {
    CheckCircle2Icon, InfoIcon, Loader2, XCircleIcon
} from 'lucide-react';
import KBTooltipContent from './KBTooltipContent';

/**
 * Handles the status display and output a badge that would display based on the status
 * @param {Object} props
 * @param {"success" | "failed" | "new" | string} props.status
 * @param {string} props.tooltipText
 * @returns {React.JSX}
 */
export function BadgeStatus({
    status, tooltipText
}) {
    const normalizedStatus = (status || '').toLowerCase();

    if (['processing', 'uploading', 'checking'].includes(normalizedStatus)) {
        return (
            <div className="flex gap-1 justify-center items-center">
                <Badge className="bg-yellow-500 text-white hover:bg-yellow-400 flex items-center gap-1">
                    <Loader2 size={12} className="animate-spin" />
                    Processing
                </Badge>
            </div>
        );
    }

    const STATUS_MAPPER = {
        success: 'Successful',
        completed: 'Successful',
        failed: 'Failed',
        error: 'Failed',
        cancelled: 'Failed',
        new: 'New'
    };

    const statusHandler = () => {
        return STATUS_MAPPER[normalizedStatus] || '--';
    };

    const colorHandler = () => {
        switch (normalizedStatus) {
            case 'success':
            case 'completed':
                return 'bg-green-500 text-white hover:bg-green-300';
            case 'failed':
            case 'error':
            case 'cancelled':
                return 'bg-red-500 text-white hover:bg-red-300';
            default:
                return 'bg-muted text-foreground hover:bg-muted/80';
        }
    };

    const iconAdder = () => {
        switch (normalizedStatus) {
            case 'failed':
            case 'error':
            case 'cancelled':
                return <XCircleIcon size={15} color={'white'} />;
            case 'success':
            case 'completed':
                return <CheckCircle2Icon size={15} color={'white'} />;
            default:
                return null;
        }
    };

    return (
        <>
            <div className="flex gap-1 justify-center items-center">
                {tooltipText ? <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger disabled>
                            <InfoIcon size={18} />
                        </TooltipTrigger>
                        {tooltipText
                            ? <TooltipContent className="bg-popover border-border border text-popover-foreground shadow-md text-sm z-50">
                                <div className="flex items-center gap-1">
                                    {iconAdder()}
                                    <p>{tooltipText}</p>
                                </div>
                            </TooltipContent>
                            : null}
                    </Tooltip>
                </TooltipProvider> : null}
                <Badge
                    data-status={normalizedStatus}
                    className={colorHandler()}>
                    {statusHandler()}
                </Badge>
            </div>
        </>
    );
}

/**
 * Handles the status display and output a text and an icon that would display based on the status
 * @param {Object} props
 * @param {"success" | "failed" | "new" | string} props.status
 * @returns {React.JSX}
 */
export function StatusDisplay({
    status, tooltipText
}) {
    const normalizedStatus = (status || '').toLowerCase();

    if (['processing', 'uploading', 'checking'].includes(normalizedStatus)) {
        return (
            <div className="flex gap-1 items-center text-yellow-600">
                <Loader2 size={17} className="animate-spin flex-shrink-0" />
                <p>Processing</p>
            </div>
        );
    }

    const STATUS_MAPPER = {
        success: 'Successful',
        completed: 'Successful',
        failed: 'Failed',
        error: 'Failed',
        cancelled: 'Failed',
        new: '--'
    };

    const statusHandler = () => {
        return STATUS_MAPPER[normalizedStatus] || '--';
    };

    const colorHandler = () => {
        switch (normalizedStatus) {
            case 'failed':
            case 'error':
            case 'cancelled':
                return 'text-red-500';
            case 'success':
            case 'completed':
                return 'text-green-500';
            default:
                return 'text-foreground';
        }
    };

    const iconAdder = () => {
        const ICON_SIZE = 17;
        switch (normalizedStatus) {
            case 'failed':
            case 'error':
            case 'cancelled':
                return <XCircleIcon className="flex-shrink-0" size={ICON_SIZE} color={'red'} />;
            case 'success':
            case 'completed':
                return <CheckCircle2Icon className="flex-shrink-0" size={ICON_SIZE} color={'green'} />;
            default:
                return null;
        }
    };

    const tooltipIconHandler = () => {
        const ICON_SIZE = 17;
        switch (normalizedStatus) {
            case 'failed':
            case 'error':
            case 'cancelled':
                if (!tooltipText) return <XCircleIcon className="flex-shrink-0" size={ICON_SIZE} color={'red'} />;
                return <InfoIcon className="flex-shrink-0" size={ICON_SIZE} color={'red'} />;
            case 'success':
            case 'completed':
                return <CheckCircle2Icon className="flex-shrink-0" size={ICON_SIZE} color={'green'} />;
            default:
                return null;
        }
    };

    return (
        <>
            <div className="flex gap-1 items-center">
                {tooltipText ? <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger disabled>
                            {tooltipIconHandler()}
                        </TooltipTrigger>
                        {tooltipText
                            ? <KBTooltipContent icon={iconAdder()} tooltipText={tooltipText} />
                            : null}
                    </Tooltip>
                </TooltipProvider> : tooltipIconHandler()}
                <p className={colorHandler()}>{statusHandler()}</p>
            </div>
        </>
    );
}